<template>
	<div class="capacity-dial-wrapper">
		<svg
			class="capacity-dial"
			viewBox="0 24 320 172"
			role="img"
			aria-labelledby="cam-dial-readout"
		>
			<path
				class="dial-arc dial-track"
				d="M 30 170 A 130 130 0 0 1 290 170"
				pathLength="100"
			/>
			<path
				class="dial-arc dial-seg-before"
				d="M 30 170 A 130 130 0 0 1 290 170"
				pathLength="100"
				:stroke-dasharray="beforeDasharray"
			/>
			<path
				class="dial-arc dial-seg-cam"
				d="M 30 170 A 130 130 0 0 1 290 170"
				pathLength="100"
				:stroke-dasharray="camDasharray"
			/>
			<line
				class="dial-today"
				x1="16"
				y1="170"
				x2="46"
				y2="170"
				:style="{ transform: todayTransform }"
			/>
			<g class="dial-needle" :style="{ transform: needleTransform }">
				<line x1="160" y1="170" x2="52" y2="170" />
			</g>
			<circle class="dial-hub" cx="160" cy="170" r="9" />
			<text class="dial-end" x="30" y="192" text-anchor="middle">0</text>
			<text class="dial-end" x="290" y="192" text-anchor="middle">{{ formattedMax }}</text>
		</svg>

		<p id="cam-dial-readout" class="has-text-centered mt-1 mb-0 capacity-dial-readout" aria-live="polite">
			<span class="is-block is-size-1 has-text-weight-bold has-text-navy capacity-dial-big">
				<NumberAnimation
					:from="capacity.afterHours"
					:to="capacity.afterHours"
					:duration="0.35"
					:format="formatNumber"
					easing="easeOutQuad"
				/>
			</span>
			<span class="is-block is-size-6 has-text-weight-semibold has-text-navy">
				hours {{ capacity.share === 0 ? 'without' : 'with' }} Cam
			</span>
		</p>

		<div class="is-flex is-flex-direction-column mt-2 mb-0 is-size-7 has-text-grey capacity-dial-key">
			<span class="capacity-dial-key-item">
				Today (<NumberAnimation
					:from="capacity.beforeHours"
					:to="capacity.beforeHours"
					:duration="0.35"
					:format="formatNumber"
					easing="easeOutQuad"
				/>h)
			</span>
			<span class="capacity-dial-key-item is-cam">
				Extra with Cam (+<NumberAnimation
					:from="extraHours"
					:to="extraHours"
					:duration="0.35"
					:format="formatNumber"
					easing="easeOutQuad"
				/>h)
			</span>
		</div>
	</div>
</template>

<script>

import NumberAnimation from 'vue-number-animation';

import { formatNumber } from '../calculations.js';

export default {
	components: {
		NumberAnimation,
	},

	props: {
		capacity: {
			type: Object,
			required: true,
		},
	},

	computed: {
		beforeDasharray() {
			return `${this.capacity.beforeFraction.toFixed(2)} 100`;
		},

		camDasharray() {
			const before = this.capacity.beforeFraction.toFixed(2);
			const delta = Math.max(0, this.capacity.afterFraction - this.capacity.beforeFraction).toFixed(2);
			return `0 ${before} ${delta} 100`;
		},

		todayTransform() {
			return `rotate(${(this.capacity.beforeFraction * 1.8).toFixed(2)}deg)`;
		},

		needleTransform() {
			return `rotate(${(this.capacity.afterFraction * 1.8).toFixed(2)}deg)`;
		},

		formattedMax() {
			return formatNumber(this.capacity.dialMax);
		},

		extraHours() {
			return Math.max(0, this.capacity.afterHours - this.capacity.beforeHours);
		},
	},

	methods: {
		formatNumber,
	},
};

</script>

<style lang="scss" scoped>
@import '@gearset/web-core/styles/includes/variables';

.capacity-dial {
	display: block;
	width: 100%;
	max-width: 17rem;
	height: auto;
	margin: 0 auto;
	overflow: visible;
}

.dial-arc {
	fill: none;
	stroke-width: 24;
}

.dial-track {
	stroke: $grey-lightest;
}

.dial-seg-before {
	stroke: $blue;
	transition: stroke-dasharray .35s ease;
}

.dial-seg-cam {
	stroke: $orange;
	transition: stroke-dasharray .35s ease;
}

.dial-today {
	stroke: $navy;
	stroke-width: 3;
	transform-box: view-box;
	transform-origin: 160px 170px;
	transition: transform .35s ease;
}

.dial-needle {
	transform-box: view-box;
	transform-origin: 160px 170px;
	transition: transform .35s ease;

	line {
		stroke: $navy;
		stroke-width: 5;
		stroke-linecap: round;
	}
}

.dial-hub {
	fill: $navy;
}

.dial-end {
	fill: $grey-light;
	font-size: .75rem;
	font-family: inherit;
}

.capacity-dial-big {
	line-height: 1.05;
	font-variant-numeric: tabular-nums;
}

.capacity-dial-key {
	width: fit-content;
	margin-inline: auto;
	gap: .25rem;
	font-variant-numeric: tabular-nums;
}

.capacity-dial-key-item {
	white-space: nowrap;

	&::before {
		content: '';
		display: inline-block;
		width: .75rem;
		height: .75rem;
		border-radius: 3px;
		margin-right: .375rem;
		vertical-align: -1px;
		background: $blue;
	}

	&.is-cam::before {
		background: $orange;
	}
}

@media (prefers-reduced-motion: reduce) {
	.dial-needle,
	.dial-seg-before,
	.dial-seg-cam,
	.dial-today {
		transition: none;
	}
}

</style>
